import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation, Trans } from 'react-i18next';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faWindowMaximize, faLink, faArrowRight, faArrowLeft, faXmark, faHeadset, faCalendarCheck } from '@fortawesome/free-solid-svg-icons';
import { faTelegram, faMagento, faWeebly } from '@fortawesome/free-brands-svg-icons';
import SlackLogo from '../components/SlackLogo.js';
import Integration from './integration.js';
import SocialMediaConnect from '../components/socialMediaConnect.js';
import ChannelGuide from '../components/ChannelGuide.js';
import ShopifyChannelStatus from '../components/ShopifyChannelStatus.js';
import { CHANNEL_GUIDES } from '../data/channelGuides.js';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import { canSeeWhatsApp } from '../utils/featureFlags.js';
import { ChannelModalContext } from '../contexts/ChannelModalContext.js';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';
import './ChannelsPage.css';

// One config drives both the card and its modal. `kind: 'embed'` reuses the
// Integration page scoped to a single tab (native OAuth apps: Wix / Webflow /
// WordPress); `kind: 'guide'` renders a per-platform copy/paste deploy guide
// (ChannelGuide) from CHANNEL_GUIDES; `kind: 'social'` reuses the
// SocialMediaConnect flow scoped to a single platform.
//
// `category` drives the top filter:
//   'website'    generic embed + website builders (snippet, Wix, Squarespace…)
//   'ecommerce'  online-store platforms (Shopify, BigCommerce…)
//   'cms'        content systems (WordPress, Joomla, Drupal…)
//   'social'     messaging platforms (Instagram, Slack, Telegram…)

// The two generic website-embed options, split out of the old single "Website
// snippet" card (which used Widget/iframe sub-tabs) into two separate channels.
// Both render through ChannelGuide, which builds the right snippet from `embed`:
// 'widget' = site-wide floating bubble, 'iframe' = inline embed.
const WIDGET_GUIDE = {
  key: 'website-widget',
  name: 'Website widget',
  embed: 'widget',
  preview: true,
  intro: "Add the Ultimo Bots chat widget to any website. Copy the snippet, paste it once before the closing </body> tag, and a floating chat bubble appears on every page.",
  steps: [
    { title: 'Copy the widget code', body: 'Copy the snippet below. It loads a floating chat bubble in the corner of your site.', placeSnippet: true },
    { title: 'Paste it into your site', body: "Paste it right before the closing `</body>` tag, or into your site's custom code / footer area so it loads on every page." },
    { title: 'Save and publish', body: 'Save and publish your site, then reload it to confirm the chat bubble appears.' },
  ],
};
const IFRAME_GUIDE = {
  key: 'website-iframe',
  name: 'Website iframe',
  embed: 'iframe',
  preview: true,
  intro: 'Embed the Ultimo Bots chat directly inside a page as an inline element. Paste the iframe wherever you want the chat to appear.',
  steps: [
    { title: 'Copy the iframe code', body: 'Copy the iframe snippet below.', placeSnippet: true },
    { title: 'Paste it where you want the chat', body: 'Paste it into an HTML / embed block on the page where the chat should appear. Adjust the `height` value if you want a taller chat area.' },
    { title: 'Save and publish', body: 'Save and publish the page, then open it to see the inline chat.' },
  ],
};

// Native-app channels reused from the Integration page, plus the two generic
// website-embed guides above.
const WEBSITE_BASE = [
  { key: 'website-widget', name: 'Website widget', nameKey: 'channels_website_widget_name', desc: 'Add a floating chat bubble to any website with a snippet.', descKey: 'channels_website_widget_desc', kind: 'guide', category: 'website', faIcon: faComment, guide: WIDGET_GUIDE },
  { key: 'website-iframe', name: 'Website iframe', nameKey: 'channels_website_iframe_name', desc: 'Embed the chat inline on a page using an iframe.', descKey: 'channels_website_iframe_desc', kind: 'guide', category: 'website', faIcon: faWindowMaximize, guide: IFRAME_GUIDE },
  { key: 'sharelink', nameKey: 'channels_sharelink_name', descKey: 'channels_sharelink_desc', kind: 'embed', tab: 'sharelink', category: 'website', faIcon: faLink },
  { key: 'wix', name: 'Wix', descKey: 'channels_wix_desc', kind: 'embed', tab: 'wix', category: 'website', logo: '/wix-logo.png' },
  { key: 'webflow', name: 'Webflow', descKey: 'channels_webflow_desc', kind: 'embed', tab: 'webflow', category: 'website', logo: '/webflow-logo.png' },
];
const WORDPRESS_CHANNEL = { key: 'wordpress', name: 'WordPress', descKey: 'channels_wordpress_desc', kind: 'embed', tab: 'wordpress', category: 'cms', logo: '/wordpress-logo.png' };
const SOCIAL_CHANNELS = [
  { key: 'instagram', name: 'Instagram', descKey: 'channels_instagram_desc', kind: 'social', platform: 'instagram', category: 'social', logo: '/instagram-logo.webp' },
  { key: 'facebook', name: 'Facebook', descKey: 'channels_facebook_desc', kind: 'social', platform: 'facebook', category: 'social', logo: '/facebook-logo.webp' },
  { key: 'whatsapp', name: 'WhatsApp', descKey: 'channels_whatsapp_desc', kind: 'social', platform: 'whatsapp', category: 'social', logo: '/whatsapp-logo.webp' },
  { key: 'telegram', name: 'Telegram', descKey: 'channels_telegram_desc', kind: 'social', platform: 'telegram', category: 'social', faIcon: faTelegram, iconColor: '#229ED9' },
  { key: 'slack', name: 'Slack', descKey: 'channels_slack_desc', kind: 'social', platform: 'slack', category: 'social', slack: true },
];

// FontAwesome brand fallbacks for the handful of platforms simple-icons no
// longer hosts a mark for.
const FA_BRANDS = { magento: faMagento, weebly: faWeebly };

// Turn each researched guide into a channel card. Website builders fold into
// the 'website' filter; e-commerce and CMS keep their own filter buckets.
const GUIDE_CHANNELS = CHANNEL_GUIDES.map((g) => ({
  key: g.key,
  name: g.name,
  desc: g.cardDesc,
  descKey: `cguide_${g.key}_carddesc`,
  kind: 'guide',
  category: g.category === 'builder' ? 'website' : g.category,
  logo: g.logo,
  wide: g.wide,
  faIcon: g.faBrand ? FA_BRANDS[g.faBrand] : undefined,
  iconColor: g.brand,
  monogram: g.monogram,
  brand: g.brand,
  guide: g,
}));

// Shopify is promoted into the fixed lead order below (right after WordPress),
// so it is pulled out of the generic e-commerce bucket — otherwise it would be
// listed twice. `.filter` on the key keeps the rest of the bucket intact even
// if the guide is ever renamed or removed.
const SHOPIFY_CHANNEL = GUIDE_CHANNELS.find((c) => c.key === 'shopify');
const GUIDE_BUILDERS = GUIDE_CHANNELS.filter((c) => c.category === 'website');
const GUIDE_ECOMMERCE = GUIDE_CHANNELS.filter(
  (c) => c.category === 'ecommerce' && c.key !== 'shopify'
);
const GUIDE_CMS = GUIDE_CHANNELS.filter((c) => c.category === 'cms');

// Fixed lead order for the "All" view (Deploy → Channels): website widget,
// website iframe, chat link, wix, webflow, wordpress, shopify, then the social
// channels (instagram, facebook, whatsapp, telegram, slack). Everything after is
// "the rest" and its order does not matter — it stays grouped by category for the
// filter buckets.
const CHANNELS = [
  ...WEBSITE_BASE,        // website-widget, website-iframe, sharelink (chat link), wix, webflow
  WORDPRESS_CHANNEL,      // wordpress
  ...(SHOPIFY_CHANNEL ? [SHOPIFY_CHANNEL] : []),  // shopify
  ...SOCIAL_CHANNELS,     // instagram, facebook, whatsapp, telegram, slack
  ...GUIDE_BUILDERS,
  ...GUIDE_ECOMMERCE,
  ...GUIDE_CMS,
];

// Top-of-page filter. 'all' shows everything; the rest match CHANNELS.category.
const CHANNEL_FILTERS = [
  { key: 'all', labelKey: 'filter_all' },
  { key: 'website', labelKey: 'channels_filter_website' },
  { key: 'ecommerce', labelKey: 'channels_filter_ecommerce' },
  { key: 'cms', labelKey: 'channels_filter_cms' },
  { key: 'social', labelKey: 'channels_filter_social' },
];

// Warm the browser cache for the channel logos at startup (this module is
// imported eagerly by App.js) so they paint instantly when the grid mounts
// instead of popping in a beat late on first load. Slack/Telegram render as
// components/icons, so only the <img> logos need warming. Deferred to idle.
if (typeof window !== 'undefined') {
  const warmChannelLogos = () =>
    CHANNELS.forEach((ch) => {
      if (ch.logo) {
        const img = new Image();
        img.src = ch.logo;
      }
    });
  if (window.requestIdleCallback) window.requestIdleCallback(warmChannelLogos);
  else setTimeout(warmChannelLogos, 200);
}

function ChannelIcon({ ch }) {
  if (ch.logo) return <img src={ch.logo} alt="" />;
  if (ch.slack) return <SlackLogo size={30} />;
  if (ch.monogram) {
    return (
      <span className="ch-monogram" style={{ color: ch.brand || '#5616ea' }}>
        {ch.monogram}
      </span>
    );
  }
  return (
    <FontAwesomeIcon
      icon={ch.faIcon}
      className={`ch-glyph ch-glyph-${ch.key}`}
      style={ch.iconColor ? { color: ch.iconColor } : undefined}
    />
  );
}

// Reused from the Activity → Inbox filter chips: the SVG border that draws on
// when a chip becomes active. Styled by `.platform-filter-border-*`.
function FilterBorderDraw() {
  return (
    <svg className="platform-filter-border-svg" aria-hidden="true" focusable="false">
      <rect className="platform-filter-border-rect" pathLength="100" />
    </svg>
  );
}

function ChannelsPage() {
  const { t } = useTranslation();
  const { openAddBot, selectedBotId, setSelectedBotId, bots, loading } = useSelectedBot();
  // Resolve a channel's display name/desc, preferring its translation key.
  const channelName = (ch) => (ch.nameKey ? t(ch.nameKey) : ch.name);
  const channelDesc = (ch) => (ch.descKey ? t(ch.descKey) : ch.desc);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const { requestUnsavedAction } = useUnsavedChanges();
  const [modalNavigation, setModalNavigation] = useState(null);
  const [footerTarget, setFooterTarget] = useState(null);
  const modalFrame = useMemo(() => ({ footerTarget, setNavigation: setModalNavigation }), [footerTarget]);
  const requestClose = useCallback(() => {
    if (!modalNavigation?.busy) requestUnsavedAction(() => setActive(null));
  }, [modalNavigation, requestUnsavedAction]);
  const continued = useRef(false);
  useEffect(() => {
    if (continued.current || loading) return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get('botId');
    const channel = params.get('channel');
    if (!id || !bots?.some(bot => String(bot.id) === id)) return;
    const match = CHANNELS.find(item => item.key === channel);
    if (!match || (channel === 'whatsapp' && !canSeeWhatsApp())) return;
    continued.current = true;
    setSelectedBotId(id);
    setActive(match);
  }, [bots, loading, setSelectedBotId]);

  useEffect(() => {
    if (!active) return undefined;
    const handleEscape = event => {
      // Nested connect/confirmation dialogs own Escape while they are open.
      if (event.key === 'Escape' && !document.querySelector('.MuiModal-root')) requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [active, requestClose]);

  // WhatsApp is available to every customer.
  const channels = canSeeWhatsApp() ? CHANNELS : CHANNELS.filter((c) => c.key !== 'whatsapp');
  const channelCounts = CHANNEL_FILTERS.reduce((acc, f) => {
    acc[f.key] =
      f.key === 'all'
        ? channels.length
        : channels.filter((c) => c.category === f.key).length;
    return acc;
  }, {});
  const byCategory =
    filter === 'all' ? channels : channels.filter((c) => c.category === filter);
  // Free-text search layered on top of the category filter (matches name + desc).
  const q = query.trim().toLowerCase();
  const visibleChannels = q
    ? byCategory.filter(
        (c) =>
          (channelName(c) || '').toLowerCase().includes(q) ||
          (channelDesc(c) || '').toLowerCase().includes(q)
      )
    : byCategory;

  // Smoothly animate the modal's height as its content changes (card ↔ manage
  // view, async loads, snippet sub-tabs) instead of jumping.
  const morphRef = useRef(null);
  const [morphHeight, setMorphHeight] = useState(null);
  useEffect(() => {
    const el = morphRef.current;
    if (!active || !el) {
      setMorphHeight(null);
      return undefined;
    }
    let raf = 0;
    const measure = () => {
      if (!morphRef.current) return;
      const h = morphRef.current.offsetHeight;
      setMorphHeight((prev) => (prev != null && Math.abs(prev - h) < 1 ? prev : h));
    };
    measure();
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [active]);

  // Lock background page scroll while a channel modal is open (the modal has its
  // own scroll inside `.channel-modal-overlay`).
  useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);

  return (
    <div className="channels-page">
      <header className="channels-head">
        <h1 className="channels-title">{t('channels_page_title')}</h1>
        <p className="channels-sub">
          {t('channels_page_sub')}
        </p>
      </header>

      {/* Filters on the left, search on the right. `no-undraw` stays on
          permanently: this is a single-select filter, so we only ever want the
          draw-on for the newly active chip. Without it, the un-draw rule fires
          on every inactive chip (e.g. Social flashing a border when you switch
          from All to Website). */}
      <div className="channels-toolbar">
        <div className="platform-filters channels-filters no-undraw">
          {CHANNEL_FILTERS.map((f) => {
            const isActive = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                className={`platform-filter ${isActive ? 'active' : ''}`}
                onClick={() => setFilter(f.key)}
                aria-pressed={isActive}
              >
                <span className="platform-filter-count">{channelCounts[f.key]}</span>
                {t(f.labelKey)}
                <FilterBorderDraw />
              </button>
            );
          })}
        </div>
        <TextField
          size="small"
          placeholder={t('channels_search_placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="channels-search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#9a93b3' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 240,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#fff',
              '& fieldset': { borderColor: '#e6e0f5' },
              '&:hover fieldset': { borderColor: '#c4b5fd' },
              '&.Mui-focused fieldset': { borderColor: '#5616ea', borderWidth: '1px' },
            },
            '& .MuiOutlinedInput-input': { fontSize: '0.85rem' },
          }}
        />
      </div>

      {visibleChannels.length === 0 ? (
        <p className="channels-search-empty">{t('channels_no_match', { query })}</p>
      ) : (
      <div className="channels-grid">
        {visibleChannels.map((ch) => (
          <button
            key={ch.key}
            type="button"
            className="channel-card"
            onClick={() => setActive(ch)}
          >
            <span className={`channel-card-icon${ch.wide ? ' wide' : ''}`}>
              <ChannelIcon ch={ch} />
            </span>
            <span className="channel-card-name">
              {channelName(ch)}
              {ch.badge && <span className="channel-card-badge">{ch.badge}</span>}
            </span>
            <span className="channel-card-desc">{channelDesc(ch)}</span>
            <span className="channel-card-cta">
              {t('channels_card_setup')} <FontAwesomeIcon icon={faArrowRight} />
            </span>
          </button>
        ))}
      </div>
      )}

      <div className="channels-help">
        <div className="channels-help-header">
          <FontAwesomeIcon icon={faHeadset} />
          <h3>{t('need_help_integration')}</h3>
        </div>
        <p>
          <Trans i18nKey="channels_help_body_html" components={{ strong: <strong /> }} />
        </p>
        <button
          type="button"
          className="channels-help-btn"
          onClick={() =>
            window.open(
              'https://calendly.com/build-ultimo-bots/integration_support',
              '_blank'
            )
          }
        >
          <FontAwesomeIcon icon={faCalendarCheck} />
          {t('book_integration_call')}
        </button>
      </div>

      {active &&
        createPortal(
          <div className="channel-modal-overlay" onClick={requestClose}>
            <div className={`channel-modal${active.kind === 'social' ? ' channel-modal--social' : ''}`} onClick={(e) => e.stopPropagation()}>
              <div className="channel-modal-head">
                {modalNavigation && <button type="button" className="channel-modal-back"
                  aria-label={t('back')} title={t('back')} disabled={modalNavigation.busy}
                  onClick={() => requestUnsavedAction(modalNavigation.onBack)}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>}
                <span className={`channel-modal-icon${active.wide ? ' wide' : ''}`}>
                  <ChannelIcon ch={active} />
                </span>
                <span className="channel-modal-title">{channelName(active)}</span>
                <button
                  type="button"
                  className="channel-modal-close"
                  onClick={requestClose}
                  disabled={modalNavigation?.busy}
                  aria-label={t('close')}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <ChannelModalContext.Provider value={modalFrame}>
              <div className="channel-modal-body">
                <div
                  className="channel-modal-morph"
                  style={{ height: morphHeight != null ? morphHeight : 'auto' }}
                >
                  <div ref={morphRef}>
                    {active.kind === 'embed' ? (
                      <Integration only={active.tab} addBot={openAddBot} />
                    ) : active.kind === 'guide' ? (
                      active.guide && active.guide.key === 'shopify'
                        ? <ShopifyChannelStatus guide={active.guide} botId={selectedBotId} />
                        : (['website-widget', 'website-iframe'].includes(active.guide.key)
                            && bots?.find((b) => String(b.id) === String(selectedBotId))?.webflow_hosted)
                          ? <p className="integration-connect-text" data-testid="wf-no-snippet">{t('wf_no_snippet_for_webflow')}</p>
                          : <ChannelGuide guide={active.guide} botId={selectedBotId} />
                    ) : (
                      <div className="social-platforms-grid channel-social-grid">
                        <SocialMediaConnect variant="cards" onlyPlatform={active.platform} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="channel-modal-footer-slot" ref={setFooterTarget} />
              </ChannelModalContext.Provider>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default ChannelsPage;
